import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = loading, false = logged out, object = logged in
  const [user, setUser] = useState(null);
  const [currency, setCurrency] = useState("USD");

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      setCurrency(data.base_currency || "USD");
    } catch (error) {
      // Not authenticated (401) is expected on first mount before login
      if (error?.response?.status !== 401) {
        console.error("Auth refresh failed:", error);
      }
      setUser(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    setCurrency(data.base_currency || "USD");
    return data;
  }, []);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data);
    setCurrency(data.base_currency || "USD");
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (error) {
      console.error("Logout request failed:", error);
    }
    setUser(false);
  }, []);

  const changeCurrency = useCallback(async (code) => {
    await api.patch("/auth/currency", { base_currency: code });
    setCurrency(code);
    setUser((u) => (u && typeof u === "object" ? { ...u, base_currency: code } : u));
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, currency, login, register, logout, refresh, changeCurrency }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
